import React, { ChangeEvent, ReactNode } from "react";
import { omit, isNil, isEmpty } from "ramda";

import { MULTIPLIER } from "styles/theme";
import styled from "styles/styled_components";

import StyledError from "components/shared/error";
import StyledWarning from "components/shared/warning";

const StyledInput = styled.input`
  border: ${(props) => props.theme.lightBorder};
  line-height: ${() => `calc(3 * ${MULTIPLIER}px)`};
  padding: ${(props) =>
    `4px ${props.theme.spacing.small}`}; // To make sure input height is 40px
`;

const StyledInputWrapper = styled.span`
  display: flex;
  flex-direction: column;
`;

export interface IInputProps {
  autoFocus?: boolean;
  /** A reference that is added as a class to the wrapping span element, and the aria-label
   * @default ""
   */
  inputName: string;
  /** An error to be displayed below the input
   * @default ""
   */
  error?: ReactNode;
  warning?: ReactNode;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  /**
   * Applies a readOnly property to the input, disabling editing
   *
   * @default false
   */
  readOnly?: boolean;
  value: string;
}

function Input(props: IInputProps) {
  const { error = "", inputName = "", readOnly = false, warning = "" } = props;

  let message;
  if (!isNil(error) && !isEmpty(error)) {
    message = (
      <StyledError className={`${inputName}-error-text`}>{error}</StyledError>
    );
  } else if (!isNil(warning) && !isEmpty(warning)) {
    message = (
      <StyledWarning className={`${inputName}-warning-text`}>
        {warning}
      </StyledWarning>
    );
  }

  return (
    <StyledInputWrapper className={`text-input ${inputName}`}>
      <StyledInput
        readOnly={readOnly}
        {...omit(["error"], props)}
        aria-label={inputName}
      />
      {message}
    </StyledInputWrapper>
  );
}

export default Input;
